import React, { useState, useCallback } from 'react'
import { previewImport, executeImport } from '@/services/api/qualification'
import type { ImportPreview, ImportResult, MatrixMapping } from '@/types/qualification'
import { QualificationMatrixPicker } from './QualificationMatrixPicker'
import { colToLetter } from '@/utils/colLetter'

interface QualificationMatrixImportProps {
  uploadedFileId: string | null
  onImportComplete: (result: ImportResult) => void
}

type Step = 'picker' | 'preview' | 'importing' | 'done'

export const QualificationMatrixImport: React.FC<QualificationMatrixImportProps> = ({
  uploadedFileId,
  onImportComplete,
}) => {
  const [step, setStep] = useState<Step>('picker')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mapping, setMapping] = useState<MatrixMapping | null>(null)

  const handleMappingSelected = useCallback(async (newMapping: MatrixMapping) => {
    if (!uploadedFileId) return
    setError(null)
    setMapping(newMapping)
    try {
      const data = await previewImport(uploadedFileId, newMapping)
      setPreview(data)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview fehlgeschlagen')
    }
  }, [uploadedFileId])

  const handleScanError = useCallback((err: string) => {
    setError(err)
  }, [])

  const handleImport = async () => {
    if (!uploadedFileId || !mapping) return
    setStep('importing')
    setError(null)
    try {
      const result = await executeImport(uploadedFileId, undefined, mapping)
      setImportResult(result)
      onImportComplete(result)
      setStep('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import fehlgeschlagen')
      setStep('preview')
    }
  }

  const handleReset = useCallback(() => {
    setStep('picker')
    setPreview(null)
    setImportResult(null)
    setMapping(null)
    setError(null)
  }, [])

  if (!uploadedFileId) {
    return (
      <div className="text-center p-8 text-gray-500">
        <div className="text-4xl mb-4 opacity-30">&#x2B06;</div>
        <p>Lade zuerst eine Excel-Datei hoch</p>
      </div>
    )
  }

  if (error && step === 'picker' && !preview) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 font-medium">Fehler</p>
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {step === 'picker' && uploadedFileId && (
        <QualificationMatrixPicker
          uploadedFileId={uploadedFileId}
          onMappingSelected={handleMappingSelected}
          onScanError={handleScanError}
        />
      )}

      {error && step !== 'picker' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {step === 'preview' && preview && mapping && (
        <>
          <div className="bg-gray-50 border rounded-lg p-3 text-sm">
            <span className="font-medium">Mapping:</span> Subjects in Zeile {mapping.subject_header_row},
            Backgrounds in Spalte {colToLetter(mapping.background_header_col)}
            <button
              onClick={handleReset}
              className="ml-3 text-blue-600 hover:text-blue-700 underline"
            >
              Ändern
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Backgrounds (neu)" value={preview.backgrounds_to_create} color="blue" />
            <StatCard label="Backgrounds (exist.)" value={preview.backgrounds_existing} color="green" />
            <StatCard label="Subjects (neu)" value={preview.subjects_to_create} color="purple" />
            <StatCard label="Subjects (exist.)" value={preview.subjects_existing} color="gray" />
            <StatCard label="Qualifikationen" value={preview.entries_count} color="yellow" />
          </div>

          {preview.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 font-medium mb-1">Warnungen</p>
              <ul className="text-yellow-700 text-sm list-disc list-inside">
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EntityList title="Backgrounds" entities={preview.backgrounds} />
            <EntityList title="Subjects" entities={preview.subjects} />
          </div>

          <button
            onClick={handleImport}
            disabled={
              preview.entries_count === 0 &&
              preview.backgrounds.length === 0 &&
              preview.subjects.length === 0
            }
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {preview.entries_count > 0
              ? `Import durchführen (${preview.entries_count} Einträge)`
              : `Import durchführen (${preview.backgrounds.length} Hintergründe, ${preview.subjects.length} Fächer)`}
          </button>
        </>
      )}

      {step === 'importing' && (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-600">Import läuft...</p>
        </div>
      )}

      {step === 'done' && importResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-green-800 font-medium text-lg">Import abgeschlossen</p>
          <p className="text-green-700 text-sm mt-1">
            {importResult.entries_created} neue, {importResult.entries_updated} aktualisierte Einträge.
          </p>
          {importResult.backgrounds_created > 0 && (
            <p className="text-green-600 text-xs mt-1">
              {importResult.backgrounds_created} neue Hintergründe angelegt.
            </p>
          )}
          {importResult.subjects_created > 0 && (
            <p className="text-green-600 text-xs mt-1">
              {importResult.subjects_created} neue Fächer angelegt.
            </p>
          )}
          {importResult.warnings.length > 0 && (
            <div className="mt-3 text-left">
              <p className="text-yellow-800 font-medium text-sm">Warnungen</p>
              <ul className="text-yellow-700 text-xs list-disc list-inside">
                {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <button
            onClick={handleReset}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Weitere Datei importieren
          </button>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    gray: 'bg-gray-50 text-gray-600',
  }
  const textMap: Record<string, string> = {
    blue: 'text-blue-800',
    green: 'text-green-800',
    purple: 'text-purple-800',
    yellow: 'text-yellow-800',
    gray: 'text-gray-800',
  }
  return (
    <div className={`${colorMap[color] || colorMap.gray} p-4 rounded-lg text-center`}>
      <p className="text-sm">{label}</p>
      <p className={`text-2xl font-bold ${textMap[color] || textMap.gray}`}>{value}</p>
    </div>
  )
}

function EntityList({ title, entities }: { title: string; entities: Array<{ name: string; exists: boolean }> }) {
  return (
    <div>
      <h5 className="font-medium mb-2">{title}</h5>
      <div className="max-h-48 overflow-y-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {entities.slice(0, 10).map((e, i) => (
              <tr key={i}>
                <td className="px-3 py-2">{e.name}</td>
                <td className="px-3 py-2">
                  {e.exists
                    ? <span className="text-green-600">&#x2713; Existiert</span>
                    : <span className="text-blue-600">Neu</span>}
                </td>
              </tr>
            ))}
            {entities.length > 10 && (
              <tr>
                <td colSpan={2} className="px-3 py-2 text-gray-500 text-center">
                  +{entities.length - 10} weitere
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
